/**
 * Scope-specific projections of the merged 3-layer graph.
 * Incident keeps the campaign node; campaign/entity/everything cluster members in a hull.
 */

import { campaignMemberActionIds } from '../../lib/funnel';
import type { ThreatElementDocument } from '../../api-utils/elements';
import type { ForceGraphData, ForceGraphLink, ForceGraphNode } from './correlationGraph';

export type GraphScopeType = 'threat' | 'campaign' | 'entity' | 'everything';

export function isCampaignNode(node: Pick<ForceGraphNode, 'layer' | 'universalType'>): boolean {
  return node.layer === 'threat' && String(node.universalType || '').toLowerCase() === 'campaign';
}

export function isActionNode(node: Pick<ForceGraphNode, 'layer' | 'universalType'>): boolean {
  return node.layer === 'threat' && String(node.universalType || '').toLowerCase() === 'action';
}

export function isAssetNode(node: Pick<ForceGraphNode, 'layer'>): boolean {
  return node.layer === 'infrastructure' || node.layer === 'business';
}

function endpointId(end: unknown): string {
  if (end == null) return '';
  if (typeof end === 'object') {
    const obj = end as { id?: string; _id?: string };
    return String(obj.id || obj._id || '');
  }
  return String(end);
}

function emptySlice(graph: ForceGraphData): ForceGraphData {
  return {
    nodes: [],
    links: [],
    stats: {
      ...graph.stats,
      nodeCount: 0,
      linkCount: 0,
      crossLayerLinkCount: 0,
    },
  };
}

function sliceKeep(graph: ForceGraphData, keep: Set<string>): ForceGraphData {
  const nodes = graph.nodes.filter((node) => keep.has(node.id));
  const links = graph.links.filter(
    (link) => keep.has(endpointId(link.source)) && keep.has(endpointId(link.target))
  );
  return {
    nodes,
    links,
    stats: {
      ...graph.stats,
      nodeCount: nodes.length,
      linkCount: links.length,
      crossLayerLinkCount: links.filter((link) => link.crossLayer).length,
    },
  };
}

export function isCampaignAssetLink(
  link: ForceGraphLink,
  nodeById: Map<string, ForceGraphNode>
): boolean {
  const source = nodeById.get(endpointId(link.source));
  const target = nodeById.get(endpointId(link.target));
  if (!source || !target) return false;
  return (
    (isCampaignNode(source) && isAssetNode(target)) ||
    (isCampaignNode(target) && isAssetNode(source))
  );
}

export function isCampaignMemberLink(
  link: ForceGraphLink,
  nodeById: Map<string, ForceGraphNode>
): boolean {
  const source = nodeById.get(endpointId(link.source));
  const target = nodeById.get(endpointId(link.target));
  if (!source || !target) return false;
  return (
    (isCampaignNode(source) && isActionNode(target)) ||
    (isCampaignNode(target) && isActionNode(source))
  );
}

export function dropCampaignAssetLinks(graph: ForceGraphData): ForceGraphData {
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const links = graph.links.filter((link) => !isCampaignAssetLink(link, nodeById));
  return {
    ...graph,
    links,
    stats: {
      ...graph.stats,
      linkCount: links.length,
      crossLayerLinkCount: links.filter((link) => link.crossLayer).length,
    },
  };
}

function hideCampaignNodes(graph: ForceGraphData): ForceGraphData {
  const keep = new Set(
    graph.nodes.filter((node) => !isCampaignNode(node)).map((node) => node.id)
  );
  return sliceKeep(graph, keep);
}

function tagCampaignClusters(
  graph: ForceGraphData,
  campaignByMember: Map<string, ThreatElementDocument>
): ForceGraphData {
  const counts = new Map<string, number>();
  for (const node of graph.nodes) {
    if (!isActionNode(node)) continue;
    const campaignId = campaignByMember.get(node.id)?._id;
    if (campaignId) counts.set(campaignId, (counts.get(campaignId) || 0) + 1);
  }
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (!isActionNode(node)) return node;
      const campaignId = campaignByMember.get(node.id)?._id;
      if (!campaignId || (counts.get(campaignId) || 0) < 2) return node;
      return { ...node, campaignCluster: campaignId };
    }),
  };
}

function walkableAdj(graph: ForceGraphData): Map<string, Set<string>> {
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const adj = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    if (!a || !b) return;
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };
  for (const link of graph.links) {
    if (isCampaignAssetLink(link, nodeById)) continue;
    add(endpointId(link.source), endpointId(link.target));
  }
  return adj;
}

/**
 * The crown-jewel assets an incident or a campaign contains (client notes 11/2), named by the graph: an incident reaches its
 * assets directly, a campaign through its incidents. An empty list says there is none; it is not "unknown".
 */
export function crownJewelsAround(graph: ForceGraphData, threatNodeId: string, campaign: boolean): ForceGraphNode[] {
  return hopSubgraph(graph, threatNodeId, campaign ? 2 : 1).nodes.filter(
    (node) => isAssetNode(node) && node.crownJewel === true
  );
}

/**
 * What the dossier says about crown jewels for a selected incident or campaign node: the names (none is an answer), or
 * `undefined` when no asset anywhere carries the tag. Without a single tag in the tenant "no crown jewel" would be a
 * guess that looks like a fact (docs/c3/BACKEND.md), so the console says nothing instead.
 */
export function crownJewelNames(graph: ForceGraphData, node: ForceGraphNode): string[] | undefined {
  if (!graph.nodes.some((candidate) => candidate.crownJewel)) return undefined;
  return crownJewelsAround(graph, node.id, isCampaignNode(node)).map((jewel) => jewel.label);
}

export function hopSubgraph(
  graph: ForceGraphData,
  origin: string | string[],
  hops: number
): ForceGraphData {
  const originIds = (Array.isArray(origin) ? origin : [origin]).filter(Boolean);
  if (originIds.length === 0) return emptySlice(graph);

  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  const adj = walkableAdj(graph);
  const keep = new Set<string>();
  let frontier: string[] = [];
  for (const id of originIds) {
    if (!nodeIds.has(id) || keep.has(id)) continue;
    keep.add(id);
    frontier.push(id);
  }
  if (keep.size === 0) return emptySlice(graph);

  for (let depth = 0; depth < hops; depth += 1) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const neighbor of Array.from(adj.get(id) ?? new Set<string>())) {
        if (!keep.has(neighbor) && nodeIds.has(neighbor)) {
          keep.add(neighbor);
          next.push(neighbor);
        }
      }
    }
    frontier = next;
  }
  return sliceKeep(graph, keep);
}

/** Incident: action + its assets + its campaign + other actions that share those assets. */
export function incidentKeepIds(graph: ForceGraphData, originId: string): Set<string> {
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const origin = nodeById.get(originId);
  const keep = new Set<string>();
  if (!origin) return keep;
  keep.add(originId);

  const adj = walkableAdj(graph);
  const neighbors = adj.get(originId) ?? new Set<string>();
  const sharedAssets = new Set<string>();
  for (const id of Array.from(neighbors)) {
    keep.add(id);
    const node = nodeById.get(id);
    if (node && isAssetNode(node)) sharedAssets.add(id);
  }

  for (const node of graph.nodes) {
    if (!isActionNode(node) || keep.has(node.id)) continue;
    const theirs = adj.get(node.id) ?? new Set<string>();
    for (const id of Array.from(theirs)) {
      if (sharedAssets.has(id)) {
        keep.add(node.id);
        break;
      }
    }
  }
  return keep;
}

export function effectiveHopDepth(scopeType: GraphScopeType, hopDepth: number): number {
  if (scopeType === 'campaign') return Math.max(hopDepth, 2);
  return hopDepth;
}

export function projectScopedGraph(args: {
  graph: ForceGraphData;
  scopeType: GraphScopeType;
  originIds: string[];
  hopDepth: number;
  threatElements: ThreatElementDocument[];
}): ForceGraphData {
  const { graph, scopeType, originIds, hopDepth, threatElements } = args;
  const campaignByMember = new Map<string, ThreatElementDocument>();
  for (const el of threatElements) {
    if (String(el.element_type || '').toLowerCase() !== 'campaign') continue;
    for (const memberId of campaignMemberActionIds(el)) {
      if (memberId) campaignByMember.set(memberId, el);
    }
  }

  const cleaned = dropCampaignAssetLinks(graph);

  if (scopeType === 'threat') {
    const origin = originIds[0] || '';
    const keep = incidentKeepIds(cleaned, origin);
    return sliceKeep(cleaned, keep);
  }

  if (scopeType === 'everything' || originIds.length === 0) {
    return tagCampaignClusters(hideCampaignNodes(cleaned), campaignByMember);
  }

  const hops = effectiveHopDepth(scopeType, hopDepth);
  const sliced = hopSubgraph(cleaned, originIds, hops);
  return tagCampaignClusters(hideCampaignNodes(sliced), campaignByMember);
}
